import { capitalizeFirstLetter } from "@/utilities/stringFormatters";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  HourglassOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { ReactNode } from "react";
import LinkOrSpan from "../links/LinkOrSpan/LinkOrSpan";
import styles from "./StatusTag.module.scss";

export type Status =
  | "looks_good"
  | "correct"
  | "complete"
  | "incomplete"
  | "out_of_scope"
  | "issue"
  | "note"
  | "ignored"
  | "error"
  | "duplicate"
  | "identified"
  | "calculating";

export const statProperties: Record<
  Status,
  {
    color: string;
    icon: ReactNode;
    textSingular: string;
    textPlural: string;
  }
> = {
  correct: {
    color: "#52C41A",
    icon: <CheckCircleOutlined />,
    textSingular: "correct",
    textPlural: "correct",
  },
  looks_good: {
    color: "#52C41A",
    icon: <CheckCircleOutlined />,
    textSingular: "complete",
    textPlural: "complete",
  },
  complete: {
    color: "#52C41A",
    icon: <CheckCircleOutlined />,
    textSingular: "complete",
    textPlural: "complete",
  },
  out_of_scope: {
    color: "#595959",
    icon: <InfoCircleOutlined />,
    textSingular: "out of scope",
    textPlural: "out of scope",
  },
  issue: {
    color: "#fa8c16",
    icon: <ExclamationCircleOutlined />,
    textSingular: "incomplete",
    textPlural: "incomplete",
  },
  incomplete: {
    color: "#fa8c16",
    icon: <ExclamationCircleOutlined />,
    textSingular: "incomplete",
    textPlural: "incomplete",
  },
  note: {
    color: "#1677ff",
    icon: <InfoCircleOutlined />,
    textSingular: "note",
    textPlural: "notes",
  },
  ignored: {
    color: "#1677ff",
    icon: <InfoCircleOutlined />,
    textSingular: "ignored",
    textPlural: "ignored",
  },
  calculating: {
    color: "#1677ff",
    icon: <HourglassOutlined />,
    textSingular: "calculating",
    textPlural: "calculating",
  },
  identified: {
    color: "#284BE7",
    icon: <CheckCircleOutlined />,
    textSingular: "identified",
    textPlural: "identified",
  },
  error: {
    color: "#ff4d4f",
    icon: <CloseCircleOutlined />,
    textSingular: "error",
    textPlural: "errors",
  },
  duplicate: {
    color: "red",
    icon: <CloseCircleOutlined />,
    textSingular: "duplicate",
    textPlural: "duplicates",
  },
};

export type DataStatBackground = "white" | "default";

export default function StatusTag({
  number,
  type = "normal",
  status,
  showIcon,
  showHover,
  background,
  to,
}: {
  status: Status;
  type?: "icon" | "normal";
  background?: DataStatBackground;
  number?: number;
  showIcon?: boolean;
  showHover?: boolean;
  to?: string;
}) {
  const properties = statProperties[status];

  const title = capitalizeFirstLetter(
    number === 1 || !number ? properties.textSingular : properties.textPlural
  );

  return (
    <Tooltip title={type === "icon" ? title : undefined}>
      <>
        <LinkOrSpan
          to={to}
          className={`${styles.datastat} ${showHover ? styles.hover : ""} ${
            background ? styles[background] : ""
          }`}
          style={{
            color: properties.color,
          }}
        >
          {showIcon === false ? "" : properties.icon}
          {type === "normal" && (
            <span>
              {number} {title}
            </span>
          )}
        </LinkOrSpan>
      </>
    </Tooltip>
  );
}
